import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useEffect, useState } from 'react';
import type { UploadState } from './useMultiFileUpload';

type Props = {
  open: boolean;
  state: UploadState;
  onCancel: () => void;
  onClose: () => void;
  showSummary?: boolean;
};

// File-count-only granularity means a slow single file poisons the
// average; suppress any estimate over 10 minutes rather than surface
// nonsense.
const MAX_DISPLAYED_ETA_SECONDS = 10 * 60;
const MIN_SAMPLES_FOR_ETA = 2;

const AnimatedEllipsis = () => {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c + 1) % 4), 450);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      {'.'.repeat(count)}
      <span style={{ visibility: 'hidden' }}>
        {'.'.repeat(3 - count)}
      </span>
    </>
  );
};

const formatEta = (secs: number): string => {
  if (secs < 10) return 'Almost done…';
  if (secs < 60) {
    const bucketed = Math.max(5, Math.round(secs / 5) * 5);
    return `About ${bucketed} seconds remaining`;
  }
  const mins = Math.round(secs / 60);
  return `About ${mins} minute${mins === 1 ? '' : 's'} remaining`;
};

const UploadProgressDialog = ({
  open,
  state,
  onCancel,
  onClose,
  showSummary = true,
}: Props) => {
  // Tick every second so the ETA stays fresh during long single files
  // (state only changes on file completion).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (state.phase !== 'uploading') return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [state.phase]);

  const percent =
    state.total > 0 ? (state.completed / state.total) * 100 : 0;

  const etaLabel = (() => {
    if (state.phase !== 'uploading') return null;
    if (!state.startedAt) return null;
    if (state.completed < MIN_SAMPLES_FOR_ETA) return null;
    if (state.completed >= state.total) return null;
    const elapsed = (Date.now() - state.startedAt) / 1000;
    if (elapsed <= 0) return null;
    const avg = elapsed / state.completed;
    const remaining = state.total - state.completed;
    const eta = Math.max(1, Math.round(remaining * avg));
    if (eta > MAX_DISPLAYED_ETA_SECONDS) return null;
    return formatEta(eta);
  })();

  const failedFiles = state.files.filter((f) => f.status === 'error');
  const isDone = state.phase === 'done' || state.phase === 'cancelled';
  const hasFailures = failedFiles.length > 0;

  const title = (() => {
    if (state.phase === 'cancelled') return 'Upload cancelled';
    if (state.phase === 'done' && hasFailures) return 'Some uploads failed';
    if (state.phase === 'done') return 'Upload Complete!';
    return 'Uploading files';
  })();

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      aria-labelledby="upload-dialog-title"
      disableEscapeKeyDown={!isDone}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="upload-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', mb: 2 }}>
          {state.phase === 'uploading' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {state.currentFileName ? (
                <>
                  Uploading {state.currentFileName}
                  <AnimatedEllipsis />
                </>
              ) : (
                <>
                  Preparing upload
                  <AnimatedEllipsis />
                </>
              )}
            </Typography>
          )}
          {isDone && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {state.phase === 'cancelled'
                ? `Cancelled after ${state.successCount} of ${state.total} file${state.total === 1 ? '' : 's'} uploaded.`
                : hasFailures
                  ? `${state.successCount} succeeded, ${state.errorCount} failed.`
                  : state.total === 1
                    ? '1 file uploaded successfully.'
                    : `All ${state.total} files uploaded successfully.`}
            </Typography>
          )}

          {state.total > 0 && (
            <>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{ mb: 1 }}
                color={
                  isDone && hasFailures
                    ? 'warning'
                    : isDone
                      ? 'success'
                      : 'primary'
                }
              />
              <Typography variant="caption" color="text.secondary">
                {state.completed} of {state.total} files
              </Typography>
              {etaLabel && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {etaLabel}
                </Typography>
              )}
            </>
          )}

          {showSummary && isDone && hasFailures && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {failedFiles.length} file
                {failedFiles.length === 1 ? '' : 's'} could not be uploaded.
                Successful uploads have been saved; you can retry the failed
                ones.
              </Alert>
              <List dense disablePadding>
                {failedFiles.map((f) => (
                  <ListItem key={f.file.name} disableGutters>
                    <ErrorOutlineIcon
                      fontSize="small"
                      color="error"
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={f.file.name}
                      secondary={f.error ?? 'Upload failed'}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {showSummary &&
            isDone &&
            !hasFailures &&
            state.phase === 'done' && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  All files uploaded.
                </Typography>
              </Box>
            )}
        </Box>
      </DialogContent>
      <DialogActions>
        {state.phase === 'uploading' && (
          <Button onClick={onCancel} color="inherit">
            Cancel
          </Button>
        )}
        {isDone && (
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadProgressDialog;
